import { SVGProps, FC } from "react";
import { IconName } from "./IconName";
import { IconSize } from "./IconSize";

/**
 * A context return function.
 */
interface ContextFn {
  /**
   * Gets the imported data for the specified path.
   * @param path The path to get the data for.
   * @returns The data, if any.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (path: string): any;
}

/**
 * Provides a declaration for the webpack `require.context` method.
 */
interface WebpackContextRequire {
  /**
   * Creates a new webpack context.
   * @param path The path to create the context under.
   * @param deep Whether to search child directories.
   * @param pattern The pattern for files to locate.
   */

  context(path: string, deep?: boolean, pattern?: RegExp): ContextFn;
}

/**
 * The default icon provider.
 * @returns Always undefined.
 */
function DefaultIconProvider(): unknown {
  return undefined;
}

let availableIcons: ContextFn = DefaultIconProvider;

try {
  availableIcons = (require as unknown as WebpackContextRequire).context("!@svgr/webpack!./icons", true, /\.svgi$/);
} catch {
  // do nothing.
}

/**
 * Gets the icon for the specified name and size.
 * @param name The name of the icon to retrieve.
 * @param size The size of the icon to retrieve.
 * @returns The icon component or null if none found.
 */
export function getIconComponent(name: IconName, size: IconSize): FC<SVGProps<SVGSVGElement> & {title: string}> | null {
  const fallBackMap: Record<IconSize, IconSize[]> = {
    large: ["regular", "small"],
    regular: ["large", "small"],
    small: ["regular", "large"],
  };

  const triedSizes: Set<IconSize> = new Set();

  const tryGetIcon = (s: IconSize): FC<SVGProps<SVGSVGElement> & {title: string}> | null => {
    if (triedSizes.has(s)) return null;
    triedSizes.add(s);
    try {
      const module = availableIcons(`./${s}/${name}.svgi`);
      if (module?.default) return module.default;
    } catch {
      return null;
    }
    return null;
  };
  let icon = tryGetIcon(size);
  if (icon) return icon;

  const fallbacks = fallBackMap[size] ?? [];
  for (const fallBackSize of fallbacks) {
    icon = tryGetIcon(fallBackSize);
    if (icon) return icon;
  }

  return null;
}
